import { useState, useEffect, useRef } from 'react';

export default function WhenToChoose() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const reasons = [
    {
      icon: 'ri-shield-check-line',
      title: 'Làm chủ hệ thống',
      description: 'Kiểm soát hoàn toàn công nghệ và dữ liệu'
    },
    {
      icon: 'ri-flow-chart',
      title: 'Chuẩn hóa quy trình',
      description: 'Xây dựng quy trình vận hành tối ưu'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Đầu tư dài hạn, bền vững',
      description: 'Hệ thống phát triển cùng doanh nghiệp'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-24 md:py-32 bg-gradient-to-br from-white via-gray-50 to-blue-50/30 relative overflow-hidden"
    >
      {/* Animated background orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#06b6d4]/5 rounded-full blur-3xl animate-[float_8s_ease-in-out_infinite]"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00c897]/5 rounded-full blur-3xl animate-[float_10s_ease-in-out_infinite]" style={{ animationDelay: '2s' }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 md:mb-20 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00c897]/10 to-[#06b6d4]/10 px-6 py-3 rounded-full mb-6 border border-[#00c897]/20 backdrop-blur-sm shadow-sm">
            <i className="ri-arrow-right-line text-[#00c897] text-xl animate-pulse"></i>
            <span className="text-[#00c897] font-semibold text-sm tracking-wide">GIẢI PHÁP PHÙ HỢP</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#2a4b7c] mb-6 leading-tight max-w-6xl mx-auto">
            <span className="inline">Phát triển phần mềm theo yêu cầu</span>{' '}
            <span className="text-[#06b6d4] inline">là lựa chọn khi doanh nghiệp cần</span>
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {reasons.map((reason, index) => (
            <div 
              key={index} 
              className={`group relative transition-all duration-1000 delay-${300 + index * 200} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${300 + index * 200}ms` }}
            >
              {/* Card */}
              <div className="relative h-full bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-[#06b6d4]/30 hover:-translate-y-2">
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-[#06b6d4] opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500"></div>
                
                {/* Icon */}
                <div className="relative flex justify-center mb-6">
                  <div className="relative">
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-[#06b6d4] rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
                    
                    {/* Icon container */}
                    <div className="relative w-24 h-24 flex items-center justify-center bg-[#06b6d4] rounded-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                      <i className={`${reason.icon} text-4xl text-white group-hover:scale-110 transition-transform duration-500`}></i>
                      
                      {/* Shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-[#2a4b7c] mb-4 group-hover:text-[#06b6d4] transition-colors duration-300 whitespace-nowrap">
                    {reason.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                    {reason.description}
                  </p>
                </div>

                {/* Decorative corner element */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-[#06b6d4] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </section>
  );
}